import { contrastRatio, parseColor } from "./color.js";
import { mixOklab, oklchToRgb, rgbToOklch, toHex } from "./oklch.js";
//#region src/lib/accent.ts
/**
* The default pack's backgrounds per mode, first the surface -subtle mixes towards:
* --color-surface, then -bg, -surface-raised, -surface-sunken, -surface-hover and
* -input-bg. A pack whose backgrounds differ declares its own (BrandPack.surfaces).
*/
var DEFAULT_SURFACES = {
	light: [
		"#ffffff",
		"#f1f5f9",
		"#f8fafc"
	],
	dark: [
		"#0f172a",
		"#020617",
		"#1e293b",
		"#0b1324"
	]
};
var WHITE = parseColor("#ffffff");
/** The near-black text colour (--palette-neutral-900). */
var INK = parseColor("#0f172a");
var TEXT_MIN = 4.5;
var UI_MIN = 3;
var STEP = .005;
/** The lightness step from base to -hover, and from -hover to -active. */
var STATE_STEP = .06;
/** The share of the base in -subtle, per mode. */
var SUBTLE_AMOUNT = {
	light: .1,
	dark: .2
};
var contrast = contrastRatio;
function shiftLightness(color, delta) {
	const oklch = rgbToOklch(color);
	return oklchToRgb({
		...oklch,
		l: oklch.l + delta
	});
}
/**
* Steps lightness in `direction` (+1 lighter, -1 darker) until `ok` passes or
* lightness runs out; returns the last colour tried. Lightness is stepped in OKLCH and
* accumulated there, not re-read from the rounded RGB, so a step too small to change
* the RGB (near black or white) still adds up.
*/
function nudge(color, direction, ok) {
	const start = rgbToOklch(color);
	let current = color;
	for (let l = start.l; !ok(current);) {
		l += direction * STEP;
		if (l < 0 || l > 1) break;
		current = oklchToRgb({
			...start,
			l
		});
	}
	return current;
}
function parseAccent(hex) {
	const color = parseColor(hex);
	if (!color || !hex.trim().startsWith("#")) throw new Error(`Invalid brand accent "${hex}"`);
	return {
		...color,
		a: 1
	};
}
/** Derives the primary ramp for one mode from a tenant accent. */
function deriveAccentRamp(hex, { mode, surface }) {
	const original = parseAccent(hex);
	const surfaces = [surface ?? DEFAULT_SURFACES[mode]].flat().map(parseAccent);
	const surfaceColor = surfaces[0];
	/** The lowest contrast between a colour and any of the surfaces. */
	const onSurfaces = (c) => Math.min(...surfaces.map((s) => contrast(c, s)));
	const away = contrast(WHITE, surfaceColor) > contrast(INK, surfaceColor) ? 1 : -1;
	let base = nudge(original, away, (c) => onSurfaces(c) >= UI_MIN);
	const fg = contrast(WHITE, base) >= contrast(INK, base) ? WHITE : INK;
	const awayFromFg = fg === WHITE ? -1 : 1;
	base = nudge(base, awayFromFg, (c) => contrast(fg, c) >= TEXT_MIN && onSurfaces(c) >= UI_MIN);
	const stateDirection = (awayFromFg === 1 ? 1 - rgbToOklch(base).l : rgbToOklch(base).l) >= STATE_STEP * 2 ? awayFromFg : -awayFromFg;
	const hover = shiftLightness(base, stateDirection * STATE_STEP);
	const active = shiftLightness(base, stateDirection * STATE_STEP * 2);
	const subtle = mixOklab(base, surfaceColor, SUBTLE_AMOUNT[mode]);
	const subtleFg = nudge(base, away, (c) => contrast(c, subtle) >= TEXT_MIN && onSurfaces(c) >= TEXT_MIN);
	const link = onSurfaces(base) >= TEXT_MIN ? base : subtleFg;
	const used = toHex(base);
	return {
		mode,
		original: toHex(original),
		used,
		adjusted: used !== toHex(original),
		ramp: {
			primary: used,
			primaryHover: toHex(hover),
			primaryActive: toHex(active),
			primaryFg: toHex(fg),
			primarySubtle: toHex(subtle),
			primarySubtleFg: toHex(subtleFg),
			link: toHex(link)
		}
	};
}
/** The custom property each ramp member is written to, per mode. */
function accentProperties(mode) {
	const prefix = `--accent-${mode}`;
	return {
		primary: `${prefix}-primary`,
		primaryHover: `${prefix}-primary-hover`,
		primaryActive: `${prefix}-primary-active`,
		primaryFg: `${prefix}-primary-fg`,
		primarySubtle: `${prefix}-primary-subtle`,
		primarySubtleFg: `${prefix}-primary-subtle-fg`,
		link: `${prefix}-link`
	};
}
var MODES = ["light", "dark"];
/**
* The inline custom properties that apply a tenant accent: --accent-light-* and
* --accent-dark-* for both modes, and the raw hex as --brand-accent. Pure, so ThemeRoot
* can render them (server rendering included); applyBrandAccent writes them to an
* element.
*/
function brandAccentStyle(hex, options = {}) {
	const derived = MODES.map((mode) => deriveAccentRamp(hex, {
		mode,
		...options.surfaces?.[mode] ? { surface: options.surfaces[mode] } : {}
	}));
	const style = {};
	for (const { mode, ramp } of derived) {
		const properties = accentProperties(mode);
		for (const key of Object.keys(ramp)) style[properties[key]] = ramp[key];
	}
	const [light, dark] = derived;
	style["--brand-accent"] = light.original;
	return {
		style,
		result: {
			original: light.original,
			adjusted: light.adjusted || dark.adjusted,
			used: {
				light: light.used,
				dark: dark.used
			}
		}
	};
}
/**
* Applies a tenant accent to an element (usually <html>, or ThemeRoot's element).
* Both modes are precomputed and written inline as --accent-light-* and
* --accent-dark-*, which the semantic layer reads, so the accent survives mode switches
* and overrides any brand pack. The raw hex goes to --brand-accent for decorative
* fills. Passing null removes the override and restores the pack's primary.
*
* Apply it to the element that carries data-theme (and data-brand), or an ancestor of
* it: the semantic tokens resolve there, so an accent set on a plain descendant has no
* effect.
*/
function applyBrandAccent(element, hex, options = {}) {
	if (hex === null) {
		for (const mode of MODES) for (const property of Object.values(accentProperties(mode))) element.style.removeProperty(property);
		element.style.removeProperty("--brand-accent");
		element.removeAttribute("data-accent");
		return null;
	}
	const { style, result } = brandAccentStyle(hex, options);
	for (const [property, value] of Object.entries(style)) element.style.setProperty(property, value);
	return result;
}
//#endregion
export { DEFAULT_SURFACES, accentProperties, applyBrandAccent, brandAccentStyle, deriveAccentRamp };

//# sourceMappingURL=accent.js.map